import type { ErrorEvent, EventHint } from "@sentry/react";

const MAX_REPORTS_PER_USER_PER_LOAD = 20;
let reportCount = 0;

/**
 * It is used to filter out errors that we can't prevent/fix.
 *
 * @remarks
 * Ensure this always returns null if you need to test Sentry locally.
 * It will allow Sentry to catch all the errors, but it won't report it.
 */
function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  if (shouldIgnoreByEvent(event) || shouldIgnoreByHint(hint)) {
    return null;
  }

  // rate limit any given user per page load
  reportCount += 1;
  if (reportCount > MAX_REPORTS_PER_USER_PER_LOAD) {
    return null;
  }

  return event;
}

function shouldIgnoreByEvent({ exception, request }: ErrorEvent): boolean {
  const requestUrl = request?.url;
  const firstException = exception?.values?.[0];
  const exceptionStacktrace = firstException?.stacktrace;
  const exceptionValue = firstException?.value;
  const exceptionType = firstException?.type;

  /**
   * Map tiles fetch aborted, Mobile Safari Webkit issue.
   * @see https://github.com/mapbox/mapbox-gl-js/issues/8480
   */
  if (exceptionType === "AbortError" && exceptionValue === "Fetch is aborted") {
    return true;
  }

  /**
   * Trying to run a map that is already running.
   * No customer impact but a known issue.
   */
  if (exceptionType === "Error" && exceptionValue === "Attempting to run(), but is already running.") {
    return true;
  }

  // Map tiles fail to fetch quite often, this is basically out of our control.
  if (
    (exceptionStacktrace?.frames?.[0]?.module === "mapbox-gl/dist/mapbox-gl" ||
      exceptionStacktrace?.frames?.[0]?.filename?.includes("mapbox-gl/dist/mapbox-gl")) &&
    exceptionValue === "Failed to fetch"
  ) {
    return true;
  }

  if (
    exceptionStacktrace?.frames?.[1]?.module === "mapbox-gl/dist/mapbox-gl" ||
    exceptionStacktrace?.frames?.[1]?.filename?.includes("mapbox-gl/dist/mapbox-gl")
  ) {
    return true;
  }

  // Block all isMoving errors that come from the map library and does not affect usage
  if (exceptionValue?.includes("Cannot read properties of undefined (reading 'isMoving')")) {
    return true;
  }

  // Block all framebuffer errors that cannot be replicated and comes from libraries
  if (exceptionValue?.toLocaleLowerCase().match(/framebuffer error \d/)) {
    return true;
  }

  // Block all easeFrame errors that comes from the map library
  if (exceptionValue?.includes("this._onEaseFrame is not a function")) {
    return true;
  }

  // Block all Non-Error exception captured with keys: _pendo_*****, currentTarget, detail error, which is silently captured and not affecting user experience
  if (exceptionValue?.includes("Non-Error exception captured with keys: _pendo_")) {
    return true;
  }

  // Block all resize observer loop problems from tracking page
  if (exceptionValue?.includes("ResizeObserver loop")) {
    return true;
  }

  // PowerBI related - Tate said it does not matter
  if (exceptionValue === '{"isTrusted":false}') {
    return true;
  }

  // sometimes people try to save the webpage and run it from their local filesystem - this is never supported.
  if (requestUrl?.startsWith("file:///")) {
    return true;
  }

  // Block all /Portal errors being sent to sentry to avoid quota overload [FED-419]
  if (requestUrl?.includes("/Portal")) {
    return true;
  }

  return false;
}

function shouldIgnoreByHint({ originalException }: EventHint): boolean {
  if (originalException instanceof Error) {
    const { message, stack } = originalException;

    // Check originalException.stack entries
    if (stack?.includes("at pendo.messageReceiver")) {
      return true;
    }

    // Chrome map layer changes
    if (stack?.includes("continuePlacement")) {
      return true;
    }

    // Cluster toggle on map
    if (stack?.includes("An image with this name already exists.")) {
      return true;
    }

    // When IE11 uses too much memory
    if (stack?.includes("Not enough storage is available to complete this operation.")) {
      return true;
    }

    // Check originalException.message entries
    // Safari map layer changes
    if (message.includes("layout.get')")) {
      return true;
    }

    // When IE11 uses too much memory
    if (message.includes("Not enough storage is available to complete this operation.")) {
      return true;
    }

    // Forgotten reasons:
    if (message.includes("undefined is not an object (evaluating '")) {
      return true;
    }

    if (message.includes("An image with this name already exists.")) {
      return true;
    }

    if (message.includes("No image with this name exists.")) {
      return true;
    }

    return false;
  }

  return false;
}

export { beforeSend };
